import React from "react";
import { Navigate } from "react-router-dom";

import { DemoProvider } from "@/features/demo/components/DemoProvider";
import { useSmartRouteResolution } from "./smart-routes/useSmartRouteResolution";

const ClassicDemoDashboard = React.lazy(() => import("@/features/demo/pages/V1DemoDashboard"));
const RealDashboardPage = React.lazy(() => import("@/features/restaurant/pages/RealDashboardPage.V3"));

const DashboardFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-100">
    <div className="text-center p-8 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-gray-700">Loading Dashboard...</h2>
      <p className="text-gray-500 mt-2">Please wait a moment.</p>
    </div>
  </div>
);

const LEGACY_REDIRECTS: Record<string, string> = {
  "/payments": "payments",
  "/qr-codes": "qr-codes",
  "/settings/staff": "settings/staff",
  "/reports": "reports",
};

export const SmartDashboardRoute: React.FC = () => {
  const debugSmartRoute = import.meta.env.DEV && import.meta.env.VITE_DASHBOARD_DEBUG === '1';

  try {
    const resolution = useSmartRouteResolution({
      routeKey: "dashboard",
      legacyRedirects: LEGACY_REDIRECTS,
    });

    if (debugSmartRoute) {
      console.debug("[SmartDashboardRoute] context:", resolution.context);
    }

    if (resolution.redirectPath) {
      return <Navigate to={resolution.redirectPath} replace />;
    }

    if (resolution.shouldRenderDemo) {
      if (debugSmartRoute) {
        console.debug("[SmartDashboardRoute] Rendering demo dashboard");
      }
      return (
        <React.Suspense fallback={<DashboardFallback />}>
          <DemoProvider>
            <ClassicDemoDashboard />
          </DemoProvider>
        </React.Suspense>
      );
    }

    if (debugSmartRoute) {
      console.debug("[SmartDashboardRoute] Rendering real dashboard");
    }
    return (
      <React.Suspense fallback={<DashboardFallback />}>
        <RealDashboardPage />
      </React.Suspense>
    );
  } catch (error) {
    console.error("SmartDashboardRoute error:", error);
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-8 bg-white rounded-lg shadow-md">
          <h2 className="text-xl font-semibold text-gray-700">Dashboard Error</h2>
          <p className="text-gray-500 mt-2">Something went wrong loading the dashboard.</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }
};
